import { PDFViewer } from '../pdf-viewer.js';
import { PDFDocument, PDFViewerState } from '../types.js';
import { router, RouteWithParams } from '../router.js';
import { apiFetch } from '../api-client.js';
import { getLastPage } from '../last-page-store.js';

export interface PDFViewerPageConfig {
  containerId: string;
  containerName: string;
  fileName: string;
  apiBaseUrl: string;
}

export class PDFViewerPage {
  private container: HTMLElement;
  private pdfViewer: PDFViewer | null = null;
  private config: PDFViewerPageConfig;
  private pdfDocument: PDFDocument | null = null;
  private downloadWired = false;

  constructor(config: PDFViewerPageConfig) {
    this.config = config;
    this.container = this.createContainer();
  }

  private createContainer(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'pdf-viewer-page';
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
      background: var(--background-primary);
      z-index: 1000;
      display: flex;
      flex-direction: column;
    `;

    // Create header with navigation
    const header = this.createHeader();
    container.appendChild(header);

    // Create PDF viewer container
    const viewerContainer = this.createViewerContainer();
    container.appendChild(viewerContainer);

    return container;
  }

  private createHeader(): HTMLElement {
    const header = document.createElement('header');
    header.className = 'pdf-viewer-header';
    header.style.cssText = `
      background: var(--primary-color);
      color: var(--text-inverse);
      padding: var(--spacing-md) var(--spacing-lg);
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-shadow: var(--shadow-md);
      flex-shrink: 0;
    `;

    // Left section: Back button and title
    const leftSection = document.createElement('div');
    leftSection.style.cssText = `
      display: flex;
      align-items: center;
      gap: var(--spacing-md);
      flex: 1;
    `;

    const backButton = document.createElement('button');
    backButton.innerHTML = '← Back';
    backButton.style.cssText = `
      background: rgba(255, 255, 255, 0.2);
      color: var(--text-inverse);
      border: 1px solid rgba(255, 255, 255, 0.3);
      padding: var(--spacing-sm) var(--spacing-md);
      border-radius: var(--border-radius-md);
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      min-height: 44px;
      min-width: 44px;
    `;

    backButton.addEventListener('mouseenter', () => {
      backButton.style.background = 'rgba(255, 255, 255, 0.3)';
    });

    backButton.addEventListener('mouseleave', () => {
      backButton.style.background = 'rgba(255, 255, 255, 0.2)';
    });

    backButton.addEventListener('click', () => {
      this.navigateBack();
    });

    const title = document.createElement('h1');
    title.id = 'pdf-viewer-title';
    title.textContent = this.config.fileName;
    title.style.cssText = `
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--text-inverse);
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `;

    leftSection.appendChild(backButton);
    leftSection.appendChild(title);

    // Right section: PDF info
    const rightSection = document.createElement('div');
    rightSection.style.cssText = `
      display: flex;
      align-items: center;
      gap: var(--spacing-md);
      font-size: 0.875rem;
    `;

    const downloadButton = document.createElement('button');
    downloadButton.id = 'pdf-download-button';
    downloadButton.type = 'button';
    downloadButton.textContent = 'Download';
    downloadButton.disabled = true;
    downloadButton.title = 'Download unavailable';
    downloadButton.style.cssText = `
      background: rgba(255, 255, 255, 0.2);
      color: var(--text-inverse);
      border: 1px solid rgba(255, 255, 255, 0.3);
      padding: var(--spacing-sm) var(--spacing-md);
      border-radius: var(--border-radius-md);
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      min-height: 44px;
      opacity: 0.6;
    `;
    downloadButton.addEventListener('mouseenter', () => {
      if (!downloadButton.disabled) {
        downloadButton.style.background = 'rgba(255, 255, 255, 0.3)';
      }
    });
    downloadButton.addEventListener('mouseleave', () => {
      downloadButton.style.background = 'rgba(255, 255, 255, 0.2)';
    });

    const pageInfo = document.createElement('span');
    pageInfo.id = 'pdf-page-info';
    pageInfo.style.cssText = `
      color: rgba(255, 255, 255, 0.9);
    `;

    rightSection.appendChild(downloadButton);
    rightSection.appendChild(pageInfo);

    header.appendChild(leftSection);
    header.appendChild(rightSection);

    return header;
  }

  private createViewerContainer(): HTMLElement {
    const viewerContainer = document.createElement('div');
    viewerContainer.id = 'pdf-viewer-full';
    viewerContainer.style.cssText = `
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      min-height: 0;
      background: var(--background-secondary);
    `;

    // Create canvas container for PDF rendering
    const canvasContainer = document.createElement('div');
    canvasContainer.className = 'canvas-container';
    canvasContainer.style.cssText = `
      flex: 1;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      background: var(--background-secondary);
      min-height: 0;
      overflow: auto;

      /* iOS-optimized touch and gesture handling */
      touch-action: pan-y pinch-zoom;
      -webkit-overflow-scrolling: touch;
      scroll-behavior: smooth;

      /* Prevent iOS Safari UI interference */
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
      -webkit-tap-highlight-color: transparent;

      /* Hardware acceleration for smoother scrolling */
      -webkit-transform: translateZ(0);
      transform: translateZ(0);
    `;

    viewerContainer.appendChild(canvasContainer);

    return viewerContainer;
  }

  private navigateBack(): void {
    const savedListPage = getLastPage(this.config.containerId, this.config.fileName);
    const pageParam = savedListPage !== null ? `?page=${savedListPage}` : '';
    const containerRoute = `/${this.config.containerId}${pageParam}` as RouteWithParams;
    router.navigate(containerRoute);
  }

  public async mount(target: HTMLElement): Promise<void> {
    // Clear and mount the container
    target.innerHTML = '';
    target.appendChild(this.container);

    // Initialize PDF viewer after DOM is mounted
    setTimeout(async () => {
      await this.initializePDFViewer();
    }, 0);
  }

  private async initializePDFViewer(): Promise<void> {
    const viewerElement = document.getElementById('pdf-viewer-full');
    if (!viewerElement) {
      console.error('PDF viewer element not found');
      return;
    }

    try {
      // Fetch the PDF document with a fresh SAS token
      this.pdfDocument = await this.fetchPDFDocument();

      if (!this.pdfDocument) {
        throw new Error('Failed to fetch PDF document');
      }

      this.wireDownloadButton(this.pdfDocument);

      // Create PDF viewer instance with state change and close callbacks
      this.pdfViewer = new PDFViewer(
        viewerElement,
        (state) => {
          this.updateViewerInfo(state);
        },
        () => {
          this.navigateBack();
        }
      );

      // Load the PDF
      await this.pdfViewer.loadPDF(this.pdfDocument);
    } catch (error) {
      console.error('Failed to initialize PDF viewer:', error);
      this.showError('Failed to load PDF. Please try again.');
    }
  }

  private async fetchPDFDocument(): Promise<PDFDocument | null> {
    try {
      const encodedFileName = encodeURIComponent(this.config.fileName);
      const response = await apiFetch(
        `${this.config.apiBaseUrl}/pdfs/${encodedFileName}?container=${this.config.containerId}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
      }

      const pdfDocument: PDFDocument = await response.json();
      return pdfDocument;
    } catch (error) {
      console.error('Failed to fetch PDF document:', error);
      return null;
    }
  }

  private updateViewerInfo(state: PDFViewerState): void {
    const pageInfo = document.getElementById('pdf-page-info');

    if (pageInfo) {
      pageInfo.textContent = `Page ${state.currentPage} of ${state.totalPages}`;
    }
  }

  private showError(message: string): void {
    const viewerElement = document.getElementById('pdf-viewer-full');
    if (!viewerElement) return;

    viewerElement.innerHTML = `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        text-align: center;
        padding: var(--spacing-2xl);
      ">
        <div style="font-size: 3rem; margin-bottom: var(--spacing-md);">⚠️</div>
        <h2 style="color: var(--text-primary); margin-bottom: var(--spacing-sm);">Error Loading PDF</h2>
        <p style="color: var(--text-secondary);">${message}</p>
        <button id="pdf-viewer-error-back" style="
          margin-top: var(--spacing-lg);
          padding: var(--spacing-md) var(--spacing-lg);
          background: var(--primary-color);
          color: var(--text-inverse);
          border: none;
          border-radius: var(--border-radius-md);
          cursor: pointer;
          font-size: 1rem;
        ">Go Back</button>
      </div>
    `;

    const backButton = viewerElement.querySelector('#pdf-viewer-error-back');
    if (backButton instanceof HTMLButtonElement) {
      backButton.addEventListener('click', () => {
        this.navigateBack();
      });
    }
  }

  private wireDownloadButton(pdf: PDFDocument): void {
    if (this.downloadWired) return;

    const button = document.getElementById('pdf-download-button');
    if (!(button instanceof HTMLButtonElement)) return;

    if (!pdf.url) return;

    const sourceUrl = pdf.url;
    const fileName = this.config.fileName;

    button.disabled = false;
    button.title = 'Download PDF';
    button.style.opacity = '1';
    button.style.cursor = 'pointer';

    button.addEventListener('click', async () => {
      // Fetch via the existing SAS URL, then trigger download from a blob: URL so
      // the <a download> attribute is honored (works without a Content-Disposition
      // header, which the SAS path cannot set without re-signing).
      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Downloading…';
      try {
        const response = await fetch(sourceUrl);
        if (!response.ok) {
          throw new Error(`Download failed: ${response.status}`);
        }
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = fileName;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      } catch (err) {
        console.error('Download failed:', err);
      } finally {
        button.disabled = false;
        button.textContent = originalText;
      }
    });

    this.downloadWired = true;
  }

  public destroy(): void {
    if (this.pdfViewer) {
      // Tear down viewer resources without triggering a second navigation.
      this.pdfViewer.close(false);
      this.pdfViewer = null;
    }
    this.container.remove();
  }
}
